import { router } from 'expo-router'
import { useCallback, useState, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View, type NativeSyntheticEvent, type TextInputChangeEventData } from 'react-native'

import { env } from '@/src/config/env'
import { Button } from '@/src/elements/button/default'
import { Input } from '@/src/elements/input/Input'
import { Icon } from '@/src/elements/media/Icon'
import { Message } from '@/src/elements/message'
import { Typography } from '@/src/elements/text/Typography'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { useAuthentication } from '../../services/useAuthentication'

export const HomeScreen = (): ReactNode => {
  const isProduction = env.EXPO_PUBLIC_APP_VARIANT === 'prod'
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const { login, loginError } = useAuthentication()
  const { routes } = useRoutes()
  const { t } = useTranslate()

  const handleEmailChange = useCallback(
    ({ nativeEvent: { text } }: NativeSyntheticEvent<TextInputChangeEventData>) => setEmail(text),
    [],
  )

  const handlePasswordChange = useCallback(
    ({ nativeEvent: { text } }: NativeSyntheticEvent<TextInputChangeEventData>) => setPassword(text),
    [],
  )

  const handleLogin = useCallback(() => {
    login({ email, password })
      .then(() => {
        router.navigate(routes['prayer-space']())
      })
      .catch(() => {}) // error is surfaced via `loginError`
  }, [login, email, password, routes])

  // In production, user should never see this page
  // This is a temporary page to inform users that the app is under maintenance
  // otherwise storybook link is displayed
  if (isProduction) {
    return (
      <View className="h-screen w-screen items-center justify-center native:pt-safe-insets-t">
        <>
          <Icon name={ICON.ALERT_TRIANGLE} className="mb-8 h-24 w-24 color-secondary-label-faded" />
          <Message colors="primary" textClassName="flex-col" className="w-auto">
            <FormattedMessage id="app.maintenance" />
          </Message>
        </>
      </View>
    )
  }

  return (
    <View className="flex-1 items-center justify-center bg-white px-6" testID={TEST_ID.ME.AUTHENTICATION.SCREEN}>
      <View className="w-full max-w-sm items-center gap-4 space-y-6">
        <View className="mb-2 items-center">
          <Typography className="text-2xl font-bold text-gray-800">
            {t('auth.login.welcome') ?? 'Bienvenue 👋'}
          </Typography>
        </View>

        <Input
          className="w-full"
          placeholder="email"
          autoCapitalize="none"
          autoCorrect={false}
          onChange={handleEmailChange}
          value={email}
          testID={TEST_ID.ME.AUTHENTICATION.USERNAME_INPUT}
        />

        <Input
          className="w-full"
          placeholder="password"
          secureTextEntry
          onChange={handlePasswordChange}
          value={password}
          testID={TEST_ID.ME.AUTHENTICATION.PASSWORD_INPUT}
        />

        <Button
          colors="primary"
          variant="solid"
          fullWidth
          className="mt-2 w-full text-white"
          onPress={handleLogin}
          testID={TEST_ID.ME.AUTHENTICATION.LOGIN_BUTTON}
        >
          {t('auth.login.login')}
        </Button>

        {!!loginError && (
          <Typography className="mt-2 text-center text-sm text-red-500">{loginError.message}</Typography>
        )}
      </View>
    </View>
  )
}
